import {
	Box,
	Button,
	Chip,
	CircularProgress,
	Grid,
	IconButton,
	Skeleton,
	Typography,
} from '@mui/material';
import { FC, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { DateTime } from 'luxon';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { ORDERS_STATUS } from 'redux/reducers/orders/constants';
import {
	downloadEuDdpLabelRequest,
	downloadEuDdpProformaInvoiceRequest,
	getEuDdpOrderRequest,
	getEuDdpSignedProformaInvoiceRequest,
	requestEuDdpShipmentRequest,
	setEuDdpRequestShipmentError,
	uploadEuDdpProformaInvoiceRequest,
} from 'redux/reducers/euDdp/reducer';
import {
	getEuDdpLabelIsLoad,
	getEuDdpConfirmShipmentIsLoad,
	getEuDdpOrder,
	getEuDdpOrderError,
	getEuDdpOrderIsLoad,
	getEuDdpProformaInvoiceIsLoad,
	getEuDdpProformaInvoiceUploadIsLoad,
	getEuDdpRequestShipmentError,
	getEuDdpRequestShipmentIsLoad,
} from 'redux/reducers/euDdp/selectors';
import { getSavedUrl } from 'redux/reducers/auth/selectors';
import { OrderError } from 'components/Order/OrderError';
import { PATHS } from 'routes/constants';
import { OrderDetails } from './OrderDetails';
import { ShipmentDetails } from './ShipmentDetails';
import { DeliveryInformation } from './DeliveryInformation';
import { ShipmentTimeLine } from './ShipmentTimeLine';
import { AmountBreakdown } from './AmountBreakdown';
import {
	hasLastMileTracking,
	SHIPPING_STATUS_COLORS,
	SHIPPING_STATUS_FILTER,
} from 'components/EuDdp/eu-ddp-structure';
import { LastMileTrackingDialog } from 'components/EuDdp/LastMileTrackingDialog';
import { ReminderAlert } from 'components/Shipment/ReminderAlert';
import { ErrorBanner } from 'ui/ErrorBanner';
import { CloudDownload, CloudUpload, LocalShipping, Place } from '@mui/icons-material';
import {
	getSwissPostDownloadLabelRequest,
	sendSwissPostLastErrorByEmailRequest,
} from 'redux/reducers/shipments/reducer';
import { UploadProFormaDialog } from 'components/Shipment/UploadProFormaDialog';
import { EEuDdpOrderShippingStatusCode } from 'redux/reducers/euDdp/constants';
import { RequestEuDdpShipmentDialog } from './RquestEuDdpShipmentDialog';

export const EuDdpOrder: FC = () => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const params = useParams<{ orderId?: string }>();
	const { orderId } = params;
	const order = useSelector(getEuDdpOrder);
	const orderIsLoading = useSelector(getEuDdpOrderIsLoad);
	const orderError = useSelector(getEuDdpOrderError);
	const savedUrl = useSelector(getSavedUrl);
	const requestEuDdpShipmentLoader = useSelector(getEuDdpRequestShipmentIsLoad);
	const requestShipmentError = useSelector(getEuDdpRequestShipmentError);
	const confirmEuDdpShipmentLoader = useSelector(getEuDdpConfirmShipmentIsLoad);
	const downloadProFormaLoader = useSelector(getEuDdpProformaInvoiceIsLoad);
	const uploadProFormaLoader = useSelector(getEuDdpProformaInvoiceUploadIsLoad);
	const downloadFirstMileLabelLoader = useSelector(getEuDdpLabelIsLoad);
	const downloadLoader =
		requestEuDdpShipmentLoader ||
		confirmEuDdpShipmentLoader ||
		downloadProFormaLoader ||
		downloadFirstMileLabelLoader ||
		uploadProFormaLoader ||
		downloadFirstMileLabelLoader;

	const validateFields = order?.fieldsConfig?.every((field) => field.isMatchRegexp);
	const validateCustomDescription = order?.orderDetails?.rows?.every((row) =>
		row.children?.every((child) => !!child.customsDescription),
	);

	const [requestDeliveryDialogOpen, setRequestDeliveryDialogOpen] = useState(false);
	const handleRequestDeliveryDialogClose = () => setRequestDeliveryDialogOpen(false);
	const handleRequestDeliveryDialogOpen = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		setRequestDeliveryDialogOpen(true);
		handleCloseReminder();
	};
	const handleRequestDeliveryDiscount = () => {
		if (!order) return;
		dispatch(requestEuDdpShipmentRequest({ orderId: order.order.id, body: null }));
		setRequestDeliveryDialogOpen(false);
	};

	const [lastMileTrackingDialogOpen, setLastMileTrackingDialogOpen] = useState(false);
	const handleLastMileTrackingDialogClose = () => setLastMileTrackingDialogOpen(false);
	const handleLastMileTrackingDialogOpen = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		setLastMileTrackingDialogOpen(true);
	};

	const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
	const handleUploadDialogClose = () => setUploadDialogOpen(false);

	const [requestEuDdpShipmentDialogOpen, setRequestEuDdpShipmentDialogOpen] = useState(false);
	const [reminderAlertOpen, setReminderAlertOpen] = useState(true);
	const handleCloseReminder = () => setReminderAlertOpen(false);
	const handleRequestEuDdpShipmentDialogClose = () => {
		setRequestEuDdpShipmentDialogOpen(false);
	};
	const handleRequestEuDdpShipmentDialogOpen = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		setRequestEuDdpShipmentDialogOpen(true);
		handleCloseReminder();
		if (!order) return;
		dispatch(requestEuDdpShipmentRequest({ orderId: order.order.id, body: null }));
	};

	useEffect(() => {
		if (!orderId) return;
		dispatch(setEuDdpRequestShipmentError(null));
		dispatch(
			getEuDdpOrderRequest({
				orderId: Number(orderId),
			}),
		);
	}, [dispatch, orderId]);

	useEffect(
		() => () => {
			dispatch(setEuDdpRequestShipmentError(null));
		},
		[dispatch],
	);

	const handleReplayHistory = () => {
		const currentPath = window.location.pathname + window.location.search;
		if (savedUrl && savedUrl.includes(PATHS.EU_DDP_PAGE) && savedUrl !== currentPath) {
			navigate(savedUrl);
		} else {
			navigate(PATHS.EU_DDP_PAGE);
		}
	};

	const handleGoToTracking = (trackingNumber: string, e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		window.open(
			`https://tracking${window.location.origin.includes('dashboard.easproject.com') ? '' : '-dev'}.easproject.com/?tracking=${trackingNumber}`,
			'_blank',
		);
	};

	const handleDownloadLabel = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (!order?.order.trackingNumber) return;

		dispatch(
			getSwissPostDownloadLabelRequest({
				orderId: order.order.id,
				trackingNumber: order.order.trackingNumber,
			}),
		);
	};

	const handleDownloadFirstMileLabel = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (!order) return;

		dispatch(downloadEuDdpLabelRequest({ orderId: order.order.id }));
	};

	const handleDownloadInvoiceProforma = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (!order) return;

		dispatch(
			downloadEuDdpProformaInvoiceRequest({
				orderId: order.order.id,
				externalOrderId: order.order.externalOrderId,
			}),
		);
	};
	const handleDownloadSignedInvoiceProforma = (e: React.MouseEvent<HTMLElement>) => {
		e.stopPropagation();
		if (!order) return;

		dispatch(
			getEuDdpSignedProformaInvoiceRequest({
				orderId: order.order.id,
				externalOrderId: order.order.externalOrderId,
			}),
		);
	};

	const handleUploadInvoiceProforma = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!params.orderId) return;
		if (!e.target.files) return;
		const formData = new FormData();
		formData.append('File', e.target.files[0]);

		dispatch(
			uploadEuDdpProformaInvoiceRequest({
				orderId: +params.orderId,
				file: formData,
			}),
		);
		handleUploadDialogClose();
	};

	const handleOpenUploadProFormaDialog = () => {
		handleCloseReminder();
		setUploadDialogOpen(true);
	};

	const handleSupportRequest = () => {
		if (!order) return;
		dispatch(sendSwissPostLastErrorByEmailRequest({ orderId: order.order.id }));
	};

	return (
		<Box>
			{requestShipmentError && (
				<ErrorBanner
					text={requestShipmentError}
					sx={{ mt: '-20px' }}
					buttonText="Send Support request"
					onClick={handleSupportRequest}
					buttonLoading={requestEuDdpShipmentLoader}
				/>
			)}
			<Box sx={{ padding: '10px 20px 0 20px', maxWidth: '100vw', overflowX: 'auto' }}>
				<Box sx={{ minWidth: '800px' }}>
					{!!orderError && (
						<OrderError
							text={orderError ?? 'Order not found'}
							linkText="Go to the EU DDP list"
							link={PATHS.EU_DDP_PAGE}
						/>
					)}

					{!orderError && !!order && !orderIsLoading && (
						<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
							<Box sx={{ mb: 2 }}>
								<Box
									sx={{ display: 'flex', justifyContent: 'start', alignItems: 'center', gap: 1 }}
								>
									<IconButton onClick={handleReplayHistory} color="primary">
										<KeyboardBackspaceIcon />
									</IconButton>
									<Typography variant="h5" color="primary">
										{order?.order.externalOrderId}
									</Typography>
									{order?.order.status && (
										<Chip
											label={order?.order.status?.replace('_', ' ')}
											size="small"
											sx={{
												p: '0 5px',
												ml: 1,
												fontSize: '.7rem',
												height: '20px',
												backgroundColor:
													order?.order.status === ORDERS_STATUS.PAID
														? 'success.light'
														: order?.order.status === ORDERS_STATUS.AWAITING_PAYMENT
															? 'warning.light'
															: 'primary.light',
												color: '#000000a0',
											}}
										/>
									)}
									{order?.order.shippingStatus && (
										<Chip
											label={
												SHIPPING_STATUS_FILTER.find(
													(status) => status[0] === order?.order.shippingStatus,
												)?.[1] || order?.order.shippingStatus
											}
											size="small"
											sx={{
												p: '0 5px',
												fontSize: '.8rem',
												height: '20px',
												bgcolor:
													SHIPPING_STATUS_COLORS.find(
														(status) => status[0] === order?.order.shippingStatus,
													)?.[1] || 'primary.light',
												color: '#000000a0',
											}}
										/>
									)}
								</Box>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'start',
										gap: '20px',
									}}
								>
									{order?.order.saleDateProvidedByEm && (
										<Typography variant="body2" gutterBottom color="GrayText">
											Payment date:{' '}
											{DateTime.fromISO(order?.order.saleDateProvidedByEm).toFormat(
												'yyyy-MM-dd, HH:mm',
											)}
										</Typography>
									)}
									{order?.order.reportingPaymentDate && (
										<Typography variant="body2" gutterBottom color="GrayText">
											Reporting date:{' '}
											{DateTime.fromISO(order?.order.reportingPaymentDate).toFormat(
												'yyyy-MM-dd, HH:mm',
											)}
										</Typography>
									)}
								</Box>
							</Box>
							<Box
								sx={{
									mt: 1,
									display: 'flex',
									alignItems: 'center',
									gap: 1,
								}}
							>
								{order?.order?.shippingStatusCode === EEuDdpOrderShippingStatusCode.UNFULFILLED ? (
									<Box
										sx={{
											display: 'flex',
											flexDirection: 'row',
											gap: 0.5,
											alignItems: 'center',
										}}
									>
										<Typography color="error" sx={{ fontSize: '.8rem', fontWeight: 'bold' }}>
											{
												// order?.order.delivery_phone_validate
												// 	? 'Please check order information'
												// 	:
												order?.order.validateHsCode ? 'HS Codes are not valid' : ''
											}
										</Typography>
										<Box sx={{ position: 'relative' }}>
											<Button
												variant="contained"
												// size="small"
												onClick={handleRequestEuDdpShipmentDialogOpen}
												disabled={
													downloadLoader ||
													order.order.validateHsCode ||
													!validateFields ||
													!validateCustomDescription
												}
											>
												Calculate Delivery Cost
												{requestEuDdpShipmentLoader && (
													<CircularProgress
														size={24}
														sx={{
															color: '#004366',
															position: 'absolute',
															top: '50%',
															left: '50%',
															marginTop: '-12px',
															marginLeft: '-12px',
														}}
													/>
												)}
											</Button>
											<ReminderAlert
												open={reminderAlertOpen}
												onClose={handleCloseReminder}
												text={`Please select “Calculate Delivery Cost” to continue `}
											/>
										</Box>
									</Box>
								) : order?.order?.shippingStatusCode ===
										EEuDdpOrderShippingStatusCode.S_REQUESTED ||
								  order?.order?.shippingStatusCode ===
										EEuDdpOrderShippingStatusCode.PAYMENT_EXPECTED ||
								  order?.order?.shippingStatusCode ===
										EEuDdpOrderShippingStatusCode.S10_EXPECTED ? (
									<Box sx={{ position: 'relative' }}>
										<Button
											variant="contained"
											color="primary"
											// size="small"
											onClick={handleRequestEuDdpShipmentDialogOpen}
											disabled={confirmEuDdpShipmentLoader}
										>
											{order?.order?.shippingStatusCode ===
											EEuDdpOrderShippingStatusCode.S10_EXPECTED
												? 'Provide S10 Number'
												: 'Confirm Payment'}
											{confirmEuDdpShipmentLoader && (
												<CircularProgress
													size={24}
													sx={{
														color: '#004366',
														position: 'absolute',
														top: '50%',
														left: '50%',
														marginTop: '-12px',
														marginLeft: '-12px',
													}}
												/>
											)}
										</Button>
										<ReminderAlert
											open={reminderAlertOpen}
											onClose={handleCloseReminder}
											text={
												order?.order?.shippingStatusCode ===
												EEuDdpOrderShippingStatusCode.S10_EXPECTED
													? 'Please click "Provide S10 Number" to continue'
													: 'Please click "Confirm Payment" to continue'
											}
										/>
									</Box>
								) : order?.order?.shippingStatusCode ===
								  EEuDdpOrderShippingStatusCode.WAITING_PROFORMA ? (
									<>
										{order?.order.schemes !== 'IOSS-IMPORT' && (
											<Button
												variant="contained"
												color="primary"
												// size="small"
												onClick={handleDownloadInvoiceProforma}
												disabled={downloadLoader}
												startIcon={<CloudDownload />}
											>
												Download Invoice
												{downloadProFormaLoader && (
													<CircularProgress
														size={24}
														sx={{
															color: '#004366',
															position: 'absolute',
															top: '50%',
															left: '50%',
															marginTop: '-12px',
															marginLeft: '-12px',
														}}
													/>
												)}
											</Button>
										)}
										<Button
											variant="contained"
											color="primary"
											// size="small"
											tabIndex={-1}
											startIcon={<CloudUpload />}
											disabled={downloadLoader}
											sx={{ fontSize: '.7rem' }}
											onClick={(e) => {
												e.stopPropagation();
												handleOpenUploadProFormaDialog();
											}}
										>
											{order?.order.schemes === 'IOSS-IMPORT'
												? 'Upload Invoice for IOSS order'
												: 'Upload Signed Invoice'}
											{uploadProFormaLoader && (
												<CircularProgress
													size={24}
													sx={{
														color: '#004366',
														position: 'absolute',
														top: '50%',
														left: '50%',
														marginTop: '-12px',
														marginLeft: '-12px',
													}}
												/>
											)}
										</Button>
										<ReminderAlert
											open={reminderAlertOpen}
											onClose={handleCloseReminder}
											text={
												order?.order.schemes === 'IOSS-IMPORT'
													? 'Please, upload IOSS supportive document'
													: 'Please download the Pro-forma Invoice, sign it and upload it back'
											}
										/>
									</>
								) : (
									<>
										<Button
											variant="contained"
											color="primary"
											// size="small"
											onClick={handleDownloadFirstMileLabel}
											disabled={downloadLoader}
											startIcon={<CloudDownload />}
										>
											Download Label
											{downloadFirstMileLabelLoader && (
												<CircularProgress
													size={24}
													sx={{
														color: '#004366',
														position: 'absolute',
														top: '50%',
														left: '50%',
														marginTop: '-12px',
														marginLeft: '-12px',
													}}
												/>
											)}
										</Button>

										{/* <Button
											variant="contained"
											color="primary"
											size="small"
											onClick={handleDownloadLabel}
											disabled={downloadLoader}
											startIcon={<CloudDownload />}
										>
											Last Mile Label
											{downloadLabelLoader && (
												<CircularProgress
													size={24}
													sx={{
														color: '#004366',
														position: 'absolute',
														top: '50%',
														left: '50%',
														marginTop: '-12px',
														marginLeft: '-12px',
													}}
												/>
											)}
										</Button> */}

										{order?.order?.schemes !== 'IOSS-IMPORT' && (
											<Button
												variant="contained"
												color="primary"
												// size="small"
												onClick={handleDownloadSignedInvoiceProforma}
												disabled={downloadLoader}
												startIcon={<CloudDownload />}
											>
												Download Invoice
												{downloadProFormaLoader && (
													<CircularProgress
														size={24}
														sx={{
															color: '#004366',
															position: 'absolute',
															top: '50%',
															left: '50%',
															marginTop: '-12px',
															marginLeft: '-12px',
														}}
													/>
												)}
											</Button>
										)}
										<Button
											variant="contained"
											color="primary"
											// size="small"
											onClick={(e) => handleGoToTracking(order?.order.trackingNumber ?? '', e)}
											disabled={downloadLoader}
											startIcon={<LocalShipping />}
										>
											Tracking
										</Button>
										{hasLastMileTracking(order?.order?.shippingStatusCode) && (
											<Button
												variant="contained"
												color="primary"
												onClick={handleLastMileTrackingDialogOpen}
												disabled={downloadLoader}
												startIcon={<Place />}
											>
												Get last mile tracking
											</Button>
										)}
									</>
								)}
							</Box>
						</Box>
					)}
					{!orderError && (orderIsLoading || String(order?.order.id) !== orderId) && (
						<Box sx={{ mb: 2 }}>
							<Skeleton variant="rectangular" height="80px" sx={{ borderRadius: '12px' }} />
						</Box>
					)}
					{!orderError && (
						<Grid container spacing={2}>
							<Grid item xs={12} sm={12} md={8} lg={8} xl={8}>
								<OrderDetails />
								<ShipmentDetails />
							</Grid>

							<Grid item xs={12} sm={12} md={4} lg={4} xl={4}>
								<DeliveryInformation />
								<ShipmentTimeLine />
								<AmountBreakdown />
							</Grid>
						</Grid>
					)}
				</Box>
			</Box>
			<LastMileTrackingDialog
				open={lastMileTrackingDialogOpen}
				setClose={handleLastMileTrackingDialogClose}
				lastMileTrackingNumber={order?.order?.lastMileTrackingNumber ?? null}
			/>
			<RequestEuDdpShipmentDialog
				open={requestEuDdpShipmentDialogOpen}
				setClose={handleRequestEuDdpShipmentDialogClose}
			/>
			<UploadProFormaDialog
				open={uploadDialogOpen}
				setClose={handleUploadDialogClose}
				handleConfirm={handleUploadInvoiceProforma}
				title={
					order?.order.schemes === 'IOSS-IMPORT'
						? 'Please, upload IOSS supportive document'
						: 'Please upload the signed Pro-forma Invoice'
				}
			>
				{order?.order.schemes === 'IOSS-IMPORT' ? (
					<Box>
						<Typography variant="body1" gutterBottom>
							You need to upload a document that confirms the value of the goods in the order. It
							can be a commercial B2C invoice or order confirmation printed out from your store.
						</Typography>
					</Box>
				) : (
					<Box>
						<Typography variant="body1" gutterBottom>
							Sign and upload the pro forma invoice that you downloaded from EAS dashboard. This
							document is required for customs clearance.
						</Typography>
						<Typography variant="body1" gutterBottom>
							Ensure that proper signature is attached, you will not be able to change or amend the
							proforma.
						</Typography>
					</Box>
				)}
			</UploadProFormaDialog>
		</Box>
	);
};
